import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './CommsStats.css';

/* A year of events in one line: how many we ran, and how many people said yes.

   ★ The word is "answered yes", never "taking part". Two definitions of coming already exist
   in the platform — the RSVP button, and the four-leg test behind an event's own People
   screen (team player, team-less player, consumer order, live ticket) — and they legitimately
   disagree. This screen counts the button, because doing it the other way costs four to six
   queries PER EVENT and would put three hundred round-trips on a page load. Two definitions
   under one word is how a customer comes to distrust every figure on the screen.

   ★ No attendance rate. Someone is only marked attended where a host actually checked people
   in, so an average across events would report near-total absence for a company that simply
   does not scan. The per-event screen is where that number means something. */

/* ★ 'all' last, and it is not just a longer window: it is the figure the admin home's Events
   tile leads with, and every number on a tile has to be findable on the screen behind it. */
const PERIODS = ['month', 'quarter', 'year', 'all'];

const Tile = ({ n, label }) => (
  <div className="cst-tile">
    <span className="cst-tile__n">{n}</span>
    <span className="cst-tile__label">{label}</span>
  </div>
);

const EventStats = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('year');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    apiRequest('GET', `/company/events/stats?period=${period}`)
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [period, t]);

  useEffect(() => { load(); }, [load]);

  const periods = (
    <div className="ce-tabs">
      {PERIODS.map((p) => (
        <button key={p} type="button" className={`ce-tab ${period === p ? 'is-on' : ''}`}
                onClick={() => setPeriod(p)}>
          {t(`period_${p}`)}
        </button>
      ))}
    </div>
  );

  if (error) return <>{periods}<p className="bravos-cal__notice">{error}</p></>;
  if (data === null) return <>{periods}<span className="auth__spinner" /></>;

  return (
    <div className="cst">
      {periods}

      <div className="cst-tiles mt-3">
        <Tile n={data.events} label={t('statsEventsRun')} />
        <Tile n={data.going} label={t('statsAnsweredYes')} />
        <Tile n={data.not_going} label={t('statsAnsweredNo')} />
        <Tile n={data.roster} label={t('statsOnRoster')} />
      </div>
      {data.cancelled > 0 && (
        <p className="ce-note">{t('statsCancelled', { n: data.cancelled })}</p>
      )}
      <p className="ce-note">{t('statsAnsweredNote')}</p>

      {data.rows.length === 0 ? (
        <p className="ce-note mt-3">{t('statsNoEventsInPeriod')}</p>
      ) : (
        <div className="cst-breakdown mt-2">
          {data.rows.map((row) => (
            <div key={row.id} className="cst-line">
              <span className="cst-line__label">
                <button type="button" className="gl-link"
                        onClick={() => navigate(`/company-event/${row.id}/people`)}>
                  {row.name}
                </button>
                {row.is_cancelled && (
                  <span className="ce-pill ce-pill--cancelled ms-1">{t('cancelled')}</span>
                )}
              </span>
              <span className="cst-line__sub">
                {row.date ? shortDate(row.date, i18n.language) : ''}
              </span>
              <span className="cst-line__n">{row.going}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventStats;
